import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import URL from "../../URL";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";

const AuthContext = createContext();

const base64UrlDecode = (base64Url) => {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonStr = atob(base64);
  return JSON.parse(jsonStr);
};

const AuthProvider = ({ children }) => {
  const isTokenExpired = () => {
    const token = localStorage.getItem("access_token");
    if (!token) return true;

    try {
      const payload = base64UrlDecode(token.split(".")[1]);
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  };

  const isRefreshTokenExpired = () => {
    const token = localStorage.getItem("refresh_token");
    if (!token) return true;

    try {
      const payload = base64UrlDecode(token.split(".")[1]);
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  };

  const user = () => {
    const token = localStorage.getItem("access_token");
    return token ? jwtDecode(token) : null;
  };

  const [authState, setAuthState] = useState({
    isAuthenticated: !isTokenExpired(),
    user: user(),
  });

  const login = (token = null) => {
    const currentToken = token || localStorage.getItem("access_token");
    setAuthState({
      isAuthenticated: true,
      user: jwtDecode(currentToken),
    });
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setAuthState({
      isAuthenticated: false,
      user: null,
    });
  };

  const HasResource = (resourceName) => {
    return authState?.user?.roles?.hasOwnProperty(resourceName) || false;
  };

  const HasPermission = (resourceName, permission) => {
    if (HasResource(resourceName)) {
      return (
        authState?.user?.roles[resourceName]?.includes(permission) || false
      );
    }
    return false;
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh_token");

    if (!refreshToken || isRefreshTokenExpired()) {
      toast.error("Your session has expired. Please log in again.");
      logout();
      return null;
    }

    try {
      const response = await axios.post(`${URL}api/jwt/refresh`, null, {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });

      const newAccessToken = response.data.access_token;
      localStorage.setItem("access_token", newAccessToken);
      login(newAccessToken);
      return newAccessToken;
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Your session is blocked. Please contact support.");
      } else {
        toast.error("Your session has expired. Please log in again.");
      }

      logout();
      return null;
    }
  };

  // ✅ Attach Authorization header for all requests
  // ✅ Uncomment and fix the request interceptor
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem("access_token");

        // ✅ Was commented out — this is why token wasn't being attached
        if (accessToken && !isTokenExpired()) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshedToken = await refreshAccessToken();

          if (refreshedToken) {
            originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
            return axios(originalRequest);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []); // ✅ keep [] — runs once on mount

  const contextValue = {
    authState,
    login,
    logout,
    refreshAccessToken,
    isTokenExpired,
    isRefreshTokenExpired,
    HasResource,
    HasPermission,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
